import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLibrary } from '../hooks/api-hooks';
import { api } from '../lib/api';
import { Empty, ErrorState, Loading } from '../components/States';
import { formatDateTime } from '../lib/format';
import type { LibraryEntry } from '../lib/types';
import { resolveMediaUrl } from '../lib/media-url';

export function LibraryPage() {
  const library = useLibrary();
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);

  /** Skills are downloaded through a short-lived signed URL returned by the API. */
  async function downloadSkill(entry: LibraryEntry) {
    setBusySlug(entry.slug);
    setDownloadError(null);
    try {
      // The current contract includes this id directly. The product lookup keeps
      // old cached responses usable during rollout without guessing an id.
      let skillId = entry.skill?.id;
      if (!skillId) {
        const product = await api.product(entry.slug);
        skillId = product.skill?.id;
      }
      if (!skillId) {
        throw new Error('Skill này chưa có tệp tải xuống. Vui lòng thử lại sau hoặc liên hệ hỗ trợ.');
      }
      const download = await api.downloadSkill(skillId);
      const link = document.createElement('a');
      link.href = download.url;
      link.download = download.fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setDownloadError(error);
    } finally {
      setBusySlug(null);
    }
  }

  if (library.isPending) return <Loading />;
  if (library.error) return <ErrorState error={library.error} />;

  const entries = library.data ?? [];
  if (entries.length === 0) {
    return (
      <>
        <h1>Thư viện của tôi</h1>
        <Empty>
          Chưa có nội dung nào. <Link to="/">Xem khoá học và skill</Link> hoặc{' '}
          <Link to="/kich-hoat">nhập mã kích hoạt</Link>.
        </Empty>
      </>
    );
  }

  return (
    <section aria-labelledby="library-title">
      <h1 id="library-title">Thư viện của tôi</h1>
      <p className="muted">Nội dung bạn đã mua hoặc kích hoạt sẽ xuất hiện tại đây.</p>
      {downloadError !== null && <ErrorState error={downloadError} />}
      <div className="grid">
        {entries.map((entry) => (
          <article
            key={entry.id}
            className={entry.type === 'SKILL' ? 'card card-tint' : 'card'}
            aria-busy={busySlug === entry.slug}
          >
            {entry.thumbnailUrl && (
              <img className="card-thumb" src={resolveMediaUrl(entry.thumbnailUrl)} alt="" loading="lazy" />
            )}
            <div className="card-body">
              <span className="tag">{entry.type === 'COURSE' ? 'Khoá học' : 'Skill'}</span>
              <h3 className="card-title">{entry.title}</h3>
              {entry.shortDescription && <p className="card-text">{entry.shortDescription}</p>}
              <p className="muted">Sở hữu từ {formatDateTime(entry.grantedAt)}</p>
              {entry.type === 'COURSE' ? (
                <Link className="btn" to={`/hoc/${entry.slug}`}>
                  Vào học
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => void downloadSkill(entry)}
                  disabled={busySlug !== null}
                >
                  {busySlug === entry.slug ? 'Đang tạo link…' : 'Tải skill'}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
